import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import axios from 'axios'
import ChainHeader from 'components/Cards/ChainHeaderCard'
import { CreatableDistributorSelector } from 'components/CreatableDistributorSelector'
import { CreatableTagSelector } from 'components/CreatableTagSelector'
import DealsSection from 'components/Deals/DealsSection/DealsSection'
import { FBPage } from 'components/FbUI/Page/FBPage'
import { EditCompanyModal } from 'components/Modals/CompanyModal/EditCompanyModal'
import NotFoundCard from 'components/NotFoundCard/NotFoundCard'
import useDocumentTitle from 'components/useDocumentTitle'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType, IContactCompany } from 'models/contact_companies'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { handleFormError } from 'utils/handleFormError'
import { getIdWithPrefix } from 'utils/pathUtils'
import { z } from 'zod'
import ContactsTableSection from '../../../components/Cards/ContactsTableSection'
import { DoorProxySection } from '../../../components/Cards/DoorProxySection'
import FbButton from '../../../components/FbUI/FbButton'
import NotesSection from '../../../components/Notes/NotesSection/NotesSection'
import { Block } from '../../../components/UI/Block'
import confirm from '../../../components/dialogConfirm'
import * as S from './styles'
import EmailsTableSection from 'components/Cards/EmailsTableSection'
import { featureFlagService } from 'utils/featureFlagService'

const companyInfo = z.object({
  taglist: z.any().optional().nullable(),
  notes: z.string().optional().nullable(),
  distributors: z.array(z.number()).optional().nullable(),
  previous_taglist: z.any().optional().nullable(),
  previous_distributors: z.any().optional().nullable(),
})

export type CompanyInfo = z.infer<typeof companyInfo>

const serializeValues = (values: CompanyInfo) => {
  return {
    taglist:
      Array.isArray(values.taglist) && values.taglist.length > 0
        ? (values?.taglist as number[])
        : 'REMOVE',
    distributors: Array.isArray(values.distributors) ? values.distributors : [],
  } as const
}

export function CompanyPage() {
  const params = useParams()
  const prefix = params?.company_id ? params.company_id.slice(0, 2) : undefined
  const { preferences } = usePreferences()
  const [loaded, setLoaded] = useState(false)

  useLayoutEffect(() => {
    // based on prefix, redirect to the correct page
    switch (prefix) {
      case preferences?.company_type_prefix_map[ContactCompanyType.Chain]:
        window.location.href = `/chain/${params?.company_id}`
        return
      case preferences?.company_type_prefix_map[ContactCompanyType.University]:
        window.location.href = `/university/${params?.company_id}`
        return
      case preferences?.company_type_prefix_map[ContactCompanyType.K12District]:
        window.location.href = `/k12/${params?.company_id}`
        return
      case preferences?.company_type_prefix_map[ContactCompanyType.Hospital]:
        window.location.href = `/hospital/${params?.company_id}`
        return
      case preferences?.company_type_prefix_map[ContactCompanyType.NursingHome]:
        window.location.href = `/nursinghome/${params?.company_id}`
        return
      case preferences?.company_type_prefix_map[ContactCompanyType.Lodging]:
        window.location.href = `/lodging/chain/${params?.company_id}`
        return
    }

    setLoaded(true)
  }, [params?.company_id])

  if (!loaded) return null

  return <_CompanyPage params={params} />
}

function _CompanyPage(props: { params: Readonly<Params<string>> }) {
  const api = apiService()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { enableEmailIntegration } = featureFlagService()

  const [editCompany, setEditCompany] = useState<IContactCompany | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [previousDistributors, setPreviousDistributors] = useState<number[]>([])
  const [previousTaglist, setPreviousTaglist] = useState<number[]>([])

  const { preferences } = usePreferences()

  const companyID = getIdWithPrefix(
    props.params.company_id as string,
    preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]
  ) as number

  const {
    data: companyData,
    isLoading,
    isError,
    error,
  } = api.useGetCompany(companyID)

  useDocumentTitle(`${companyData?.name || 'Not Found'} (Company) - First Bite`)

  useEffect(() => {
    if (
      isError &&
      axios.isAxiosError(error) &&
      error?.response?.status === 404
    ) {
      setNotFound(true)
    }
  }, [error, isError])

  useEffect(() => {
    if (companyData?.chain) {
      // redirect to chain page
      navigate(
        `/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Chain]}${companyData?.chain}`
      )
    }
    if (companyData?.university_id) {
      // redirect to university page
      navigate(
        `/university/${preferences?.company_type_prefix_map[ContactCompanyType.University]}${companyData?.university_id}`
      )
    }
    if (companyData?.k12district_id) {
      // redirect to university page
      navigate(
        `/k12/${preferences?.company_type_prefix_map[ContactCompanyType.K12District]}${companyData?.k12district_id}`
      )
    }
  }, [companyData])
  if (companyData?.hospital_id) {
    // redirect to hospital page
    navigate(
      `/hospital/${preferences?.company_type_prefix_map[ContactCompanyType.Hospital]}${companyData?.hospital_id}`
    )
  }

  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()

  const { control, handleSubmit, reset, setError, formState } = useForm({
    defaultValues: {
      taglist: undefined,
    } as CompanyInfo,
    resolver: zodResolver(companyInfo),
  })

  useEffect(() => {
    if (companyData) {
      const mappedTaglist = companyData.taglist?.map((t) => t.id) || []
      const mappedDistributors =
        companyData.distributors?.map((distributor) => distributor.id) || []

      reset({
        taglist: mappedTaglist,
        distributors: mappedDistributors,
      })

      setPreviousDistributors(mappedDistributors)
      setPreviousTaglist(mappedTaglist)
    }
  }, [companyData])

  const mutation = useMutation({
    mutationFn: async (values: CompanyInfo) => {
      const newValues = serializeValues(values)
      return await api.bulkEditCompanyAttrs({
        contact_company_ids: [companyID],
        taglist: newValues.taglist.length > 0 ? newValues.taglist : 'REMOVE',
        distributors:
          newValues.distributors.length > 0 ? newValues.distributors : 'REMOVE',
        previous_distributors: previousDistributors,
        previous_taglist: previousTaglist,
      })
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['company', companyID!],
      })
    },
    onSettled: async (data, err) => {
      await queryClient.invalidateQueries({
        queryKey: ['company', companyID!],
      })

      if (err) {
        toast.error('Error updating company')
      } else {
        toast.success('Company updated')
      }
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    const [err] = await to(mutation.mutateAsync(values))
    if (err) {
      handleFormError(err, setError)
    }
  })

  const RightColumnHeader = useMemo(() => {
    return (
      <>
        <CreatableTagSelector
          name="taglist"
          control={control}
          label="Tags"
          placeholder="Tags"
          options={tagsOptions}
          setError={setError}
        />
        <CreatableDistributorSelector
          name="distributors"
          control={control}
          label="Distributors"
          placeholder="Distributor(s)"
          options={distributorsOptions}
          setError={setError}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {formState?.isDirty && (
            <S.SaveButton loading={formState?.isSubmitting} onClick={onSubmit}>
              Save
            </S.SaveButton>
          )}
        </div>
      </>
    )
  }, [
    companyData,
    formState,
    onSubmit,
    tagsOptions,
    distributorsOptions,
    control,
  ])

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  if (notFound) {
    return <NotFoundCard />
  }

  return (
    <FBPage className="gap-y-4" noContainer>
      <ChainHeader
        tableTypeSlug={companyData!.sub_category.slug}
        category={companyData?.category}
        subCategory={companyData?.sub_category?.name}
        name={companyData?.name}
        breadcrumbs={[{ breadcrumb: companyData?.name }]}
        phone={companyData?.phone_number}
        extension={companyData?.extension}
        address={{
          url: companyData?.google_place_url,
          addressInfo: {
            fullAddress: companyData?.address,
            city: companyData?.city,
            state: companyData?.state,
            zipcode: companyData?.zipcode,
          },
        }}
        domainField={companyData?.domain}
        userCreated
        rightColumnHeader={RightColumnHeader}
        editDomain={() => {
          setEditCompany(companyData!)
        }}
      />

      {!!editCompany && (
        <EditCompanyModal
          show={!!editCompany}
          setShow={() => setEditCompany(null)}
          company={editCompany}
        />
      )}

      {companyData?.sub_category.slug ? (
        <DealsSection
          forCompany={companyData}
          companyTypeSlug={companyData?.sub_category.slug}
          tableTypeSlug={companyData?.sub_category.slug}
          isUserCreated
        />
      ) : null}

      <NotesSection
        chainId={companyData?.chain}
        contactCompanyId={companyData?.id}
        pagination={true}
      />

      <ContactsTableSection data={companyData} />

      {companyData && enableEmailIntegration && (
        <EmailsTableSection forChainProxyId={companyData.id} />
      )}

      <DoorProxySection chainProxy={companyData} />

      <Block className={'flex items-center justify-center'}>
        <FbButton
          className="flex-shrink-0"
          variant="danger"
          onClick={async () => {
            if (
              await confirm(
                'Deleting this company will also delete any notes, tags, deals, or contacts you may have added. This action cannot be undone.',
                'Delete Company',
                undefined,
                {
                  buttonVariant: 'danger',
                  confirmLabel: 'Delete Company',
                }
              )
            ) {
              if (!companyData?.id) {
                return
              }
              const [err] = await to(api.deleteCompany(companyData.id!))
              if (err) {
                toast.error('Error deleting company')
                return
              }
              toast.success('Company deleted')
              navigate('/')
            }
          }}
        >
          Delete Company
        </FbButton>
      </Block>
    </FBPage>
  )
}
